import axios from 'axios';
import { type ChangeEvent, type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useActivateCompetition,
  useArchiveCompetition,
  useCompetitions,
  useCompleteCompetition,
  useStartAwardingCompetition,
  useRevertToJudgingCompetition,
  useReopenAwardingCompetition,
  useCreateCompetition,
  useDeleteCompetition,
  useRevertToActiveCompetition,
  useScheduleCompetition,
  useRevertToReviewCompetition,
  useStartJudgingCompetition,
  useStartReviewCompetition,
  useSetFzsPatronage,
  useUpdateCompetition,
  useUploadCompetitionLogo,
} from '@/hooks/useCompetitions';
import { useOrganizations } from '@/hooks/useOrganizations';
import { PageSpinner } from '@/components/ui/Spinner';
import { ErrorState, EmptyState, Alert } from '@/components/ui/Alert';
import { getApiErrorMessage } from '@/api/client';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { Table, type Column } from '@/components/ui/Table';
import type { Competition } from '@/api/types';
import {
  COMPETITION_LIFECYCLE_ACTIONS,
  type CompetitionLifecycleAction,
  competitionStatusLabel,
  competitionStatusTone,
  FZS_PATRONAGE_LABEL,
} from '@/lib/competitions';

/** Immediate toggle for Competition.IsFzsPatronage - one row's mutation, isolated in its own component so each row gets its own useMutation instance. */
function FzsPatronageToggle({ competition }: { competition: Competition }) {
  const setFzsPatronage = useSetFzsPatronage(competition.id);
  return (
    <Button
      variant={competition.isFzsPatronage ? 'secondary' : 'ghost'}
      size="sm"
      isLoading={setFzsPatronage.isPending}
      onClick={() => setFzsPatronage.mutate(!competition.isFzsPatronage)}
    >
      {competition.isFzsPatronage ? 'Odstrani pokroviteljstvo FZS' : 'Označi kot FZS'}
    </Button>
  );
}

interface CreateFormState {
  organizationId: string;
  name: string;
  description: string;
}

const emptyCreateForm: CreateFormState = { organizationId: '', name: '', description: '' };

interface EditFormState {
  name: string;
  description: string;
  submissionOpensAt: string;
  submissionDeadline: string;
  judgingDeadline: string;
  startDate: string;
  endDate: string;
}

const emptyEditForm: EditFormState = {
  name: '',
  description: '',
  submissionOpensAt: '',
  submissionDeadline: '',
  judgingDeadline: '',
  startDate: '',
  endDate: '',
};

/** `2024-05-01T00:00:00Z` -> `2024-05-01` for a date input; empty string otherwise. */
function toDateInputValue(iso?: string | null): string {
  return iso ? iso.slice(0, 10) : '';
}

/** `2024-05-01` from a date input -> an ISO datetime for the API, or null if cleared. */
function fromDateInputValue(value: string): string | null {
  return value ? new Date(`${value}T00:00:00.000Z`).toISOString() : null;
}

/**
 * ISO UTC instant -> a `datetime-local` input value, in the ORGANIZER'S OWN
 * local time (the browser's Date getters, e.g. getHours(), already account
 * for the local timezone/DST - CEST vs CET - so no manual offset math is
 * needed here).
 */
function toDateTimeInputValue(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * A `datetime-local` input value has no timezone of its own - the browser
 * (and so `new Date(value)`) reads it as the organizer's own local wall-clock
 * time, so `.toISOString()` below converts it to the correct UTC instant
 * regardless of whether the organizer is currently on CEST or CET. This is
 * what actually gets compared against the server's UTC clock to enforce the
 * submission deadline, so getting this conversion right (rather than forcing
 * a fixed UTC offset) matters.
 */
function fromDateTimeInputValue(value: string): string | null {
  return value ? new Date(value).toISOString() : null;
}

export function OrganizerCompetitionsPage() {
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const competitions = useCompetitions({ page, pageSize });
  const organizations = useOrganizations({ page: 1, pageSize: 100 });

  const createCompetition = useCreateCompetition();
  const [editing, setEditing] = useState<Competition | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [createForm, setCreateForm] = useState<CreateFormState>(emptyCreateForm);
  const [editForm, setEditForm] = useState<EditFormState>(emptyEditForm);
  const updateCompetition = useUpdateCompetition(editing?.id ?? '');
  const uploadLogo = useUploadCompetitionLogo(editing?.id ?? '');
  const deleteCompetition = useDeleteCompetition();

  const scheduleCompetition = useScheduleCompetition();
  const activateCompetition = useActivateCompetition();
  const startReviewCompetition = useStartReviewCompetition();
  const startJudgingCompetition = useStartJudgingCompetition();
  const revertToReviewCompetition = useRevertToReviewCompetition();
  const startAwardingCompetition = useStartAwardingCompetition();
  const revertToJudgingCompetition = useRevertToJudgingCompetition();
  const reopenAwardingCompetition = useReopenAwardingCompetition();
  const completeCompetition = useCompleteCompetition();
  const archiveCompetition = useArchiveCompetition();
  const revertToActiveCompetition = useRevertToActiveCompetition();

  const lifecycleMutations: Record<CompetitionLifecycleAction, ReturnType<typeof useScheduleCompetition>> = {
    schedule: scheduleCompetition,
    activate: activateCompetition,
    startReview: startReviewCompetition,
    startJudging: startJudgingCompetition,
    revertToReview: revertToReviewCompetition,
    startAwarding: startAwardingCompetition,
    revertToJudging: revertToJudgingCompetition,
    reopenAwarding: reopenAwardingCompetition,
    complete: completeCompetition,
    archive: archiveCompetition,
    revertToActive: revertToActiveCompetition,
  };

  function openCreate() {
    setCreateForm({
      ...emptyCreateForm,
      organizationId: organizations.data?.items[0]?.id ?? '',
    });
    setIsCreating(true);
  }

  function openEdit(c: Competition) {
    setEditForm({
      name: c.name,
      description: c.description ?? '',
      submissionOpensAt: toDateInputValue(c.submissionOpensAt),
      submissionDeadline: toDateTimeInputValue(c.submissionDeadline),
      judgingDeadline: toDateTimeInputValue(c.judgingDeadline),
      startDate: toDateInputValue(c.startDate),
      endDate: toDateInputValue(c.endDate),
    });
    setEditing(c);
  }

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    await createCompetition.mutateAsync({
      organizationId: createForm.organizationId,
      name: createForm.name,
      description: createForm.description || undefined,
    });
    setIsCreating(false);
  }

  async function handleUpdate(e: FormEvent) {
    e.preventDefault();
    await updateCompetition.mutateAsync({
      name: editForm.name,
      description: editForm.description || undefined,
      submissionOpensAt: fromDateInputValue(editForm.submissionOpensAt),
      submissionDeadline: fromDateTimeInputValue(editForm.submissionDeadline),
      judgingDeadline: fromDateTimeInputValue(editForm.judgingDeadline),
      startDate: fromDateInputValue(editForm.startDate),
      endDate: fromDateInputValue(editForm.endDate),
    });
    setEditing(null);
  }

  async function handleLogoChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    // Refresh `editing` from the response so the preview updates immediately
    // instead of showing the stale competition snapshot captured when the modal opened.
    const updated = await uploadLogo.mutateAsync(file);
    setEditing(updated);
    e.target.value = '';
  }

  function handleLifecycleAction(id: string, action: CompetitionLifecycleAction) {
    lifecycleMutations[action].mutate(id);
  }

  const columns: Column<Competition>[] = [
    {
      header: 'Naziv',
      accessor: (c) => (
        <Link to={`/competitions/${c.id}`} className="font-medium text-fg hover:underline">
          {c.name}
        </Link>
      ),
    },
    {
      header: 'Status',
      accessor: (c) => (
        <div className="flex flex-wrap gap-1.5">
          <Badge tone={competitionStatusTone(c.status)}>{competitionStatusLabel(c.status)}</Badge>
          {c.isFzsPatronage && <Badge tone="Fzs">{FZS_PATRONAGE_LABEL}</Badge>}
        </div>
      ),
    },
    {
      header: 'Rok za oddajo',
      accessor: (c) => (c.submissionDeadline ? new Date(c.submissionDeadline).toLocaleString() : '—'),
    },
    {
      header: 'Dejanja',
      accessor: (c) => (
        <div className="flex flex-wrap gap-2">
          {COMPETITION_LIFECYCLE_ACTIONS[c.status].map(({ action, label }) => (
            <Button
              key={action}
              variant="secondary"
              size="sm"
              isLoading={lifecycleMutations[action].isPending && lifecycleMutations[action].variables === c.id}
              onClick={() => handleLifecycleAction(c.id, action)}
            >
              {label}
            </Button>
          ))}
          <Link to={`/organizer/competitions/${c.id}/landing`}>
            <Button variant="ghost" size="sm">
              Razpis
            </Button>
          </Link>
          <Link to={`/organizer/competitions/${c.id}/categories`}>
            <Button variant="ghost" size="sm">
              Kategorije
            </Button>
          </Link>
          <Link to={`/organizer/competitions/${c.id}/awards`}>
            <Button variant="ghost" size="sm">
              Nagrade
            </Button>
          </Link>
          <Link to={`/organizer/competitions/${c.id}/judges`}>
            <Button variant="ghost" size="sm">
              Sodniki
            </Button>
          </Link>
          <Link to={`/organizer/competitions/${c.id}/participants`}>
            <Button variant="ghost" size="sm">
              Tekmovalci
            </Button>
          </Link>
          <Button variant="secondary" size="sm" onClick={() => openEdit(c)}>
            Uredi
          </Button>
          <FzsPatronageToggle competition={c} />
          <Button
            variant="danger"
            size="sm"
            isLoading={deleteCompetition.isPending && deleteCompetition.variables?.id === c.id}
            onClick={() => {
              if (!confirm(`Izbriši "${c.name}"?`)) return;
              deleteCompetition.mutate(
                { id: c.id },
                {
                  onError: (error) => {
                    // Entries block a plain delete; the organizer may still force it after a second, explicit confirmation.
                    if (!axios.isAxiosError(error) || !error.response?.data?.errors?.id) return;
                    if (confirm(`"${c.name}" ima oddane prispevke. Z izbrisom se trajno izbrišejo tudi vsi prispevki, ocene in nagrade. Tega ni mogoče razveljaviti. Vseeno izbrišem?`)) {
                      deleteCompetition.mutate({ id: c.id, force: true });
                    }
                  },
                },
              );
            }}
          >
            Izbriši
          </Button>
        </div>
      ),
    },
  ];

  const lifecycleError = [
    scheduleCompetition.error,
    activateCompetition.error,
    startReviewCompetition.error,
    startJudgingCompetition.error,
    revertToReviewCompetition.error,
    startAwardingCompetition.error,
    revertToJudgingCompetition.error,
    reopenAwardingCompetition.error,
    completeCompetition.error,
    archiveCompetition.error,
    revertToActiveCompetition.error,
  ].find(Boolean);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-fg">Upravljanje tekmovanj</h1>
        <Button onClick={openCreate} disabled={organizations.isLoading || !organizations.data?.items.length}>
          Novo tekmovanje
        </Button>
      </div>

      {lifecycleError && <Alert variant="error">{getApiErrorMessage(lifecycleError)}</Alert>}
      {deleteCompetition.isError && <Alert variant="error">{getApiErrorMessage(deleteCompetition.error)}</Alert>}

      {organizations.isSuccess && organizations.data.items.length === 0 && (
        <Alert variant="error">
          Še ne obstaja nobena organizacija. Za ustvarjanje tekmovanja je potrebna organizacija.
        </Alert>
      )}

      {competitions.isLoading && <PageSpinner />}
      {competitions.isError && (
        <ErrorState message={getApiErrorMessage(competitions.error)} onRetry={() => competitions.refetch()} />
      )}
      {competitions.isSuccess && competitions.data.items.length === 0 && (
        <EmptyState title="Ni še tekmovanj" description="Ustvarite svoje prvo tekmovanje za začetek." />
      )}
      {competitions.isSuccess && competitions.data.items.length > 0 && (
        <Table columns={columns} rows={competitions.data.items} getRowKey={(c) => c.id} />
      )}

      {competitions.isSuccess && competitions.data.total > pageSize && (
        <div className="flex items-center justify-center gap-3">
          <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Prejšnja
          </Button>
          <span className="text-sm text-fg-muted">Stran {page}</span>
          <Button
            variant="secondary"
            size="sm"
            disabled={page * pageSize >= competitions.data.total}
            onClick={() => setPage((p) => p + 1)}
          >
            Naslednja
          </Button>
        </div>
      )}

      {isCreating && (
        <Modal title="Novo tekmovanje" onClose={() => setIsCreating(false)}>
          <form onSubmit={handleCreate} className="flex flex-col gap-3">
            {createCompetition.isError && <Alert variant="error">{getApiErrorMessage(createCompetition.error)}</Alert>}
            <Select
              label="Organizacija"
              value={createForm.organizationId}
              onChange={(e) => setCreateForm((f) => ({ ...f, organizationId: e.target.value }))}
              required
            >
              <option value="" disabled>
                Izberite organizacijo&hellip;
              </option>
              {organizations.data?.items.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </Select>
            <Input
              label="Naziv"
              value={createForm.name}
              onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
              required
            />
            <Input
              label="Opis"
              value={createForm.description}
              onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))}
            />
            <p className="text-xs text-fg-muted">
              Nova tekmovanja se vedno začnejo v statusu Osnutek. Uporabite dejanja v vsaki vrstici za načrtovanje,
              aktivacijo in napredovanje tekmovanja, ko je pripravljeno.
            </p>
            <Button
              type="submit"
              isLoading={createCompetition.isPending}
              disabled={!createForm.organizationId || !createForm.name}
              className="self-start"
            >
              Ustvari tekmovanje
            </Button>
          </form>
        </Modal>
      )}

      {editing && (
        <Modal title={`Uredi "${editing.name}"`} onClose={() => setEditing(null)}>
          <form onSubmit={handleUpdate} className="flex flex-col gap-3">
            {updateCompetition.isError && <Alert variant="error">{getApiErrorMessage(updateCompetition.error)}</Alert>}
            <Input
              label="Naziv"
              value={editForm.name}
              onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              required
            />
            <Input
              label="Opis"
              value={editForm.description}
              onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
            />
            <Input
              label="Odprtje za oddajo"
              type="date"
              value={editForm.submissionOpensAt}
              onChange={(e) => setEditForm((f) => ({ ...f, submissionOpensAt: e.target.value }))}
            />
            <Input
              label="Rok za oddajo (datum in ura)"
              type="datetime-local"
              value={editForm.submissionDeadline}
              onChange={(e) => setEditForm((f) => ({ ...f, submissionDeadline: e.target.value }))}
            />
            <Input
              label="Rok za ocenjevanje (datum in ura)"
              type="datetime-local"
              value={editForm.judgingDeadline}
              onChange={(e) => setEditForm((f) => ({ ...f, judgingDeadline: e.target.value }))}
            />
            <Input
              label="Datum začetka"
              type="date"
              value={editForm.startDate}
              onChange={(e) => setEditForm((f) => ({ ...f, startDate: e.target.value }))}
            />
            <Input
              label="Datum konca"
              type="date"
              value={editForm.endDate}
              onChange={(e) => setEditForm((f) => ({ ...f, endDate: e.target.value }))}
            />
            <Button type="submit" isLoading={updateCompetition.isPending} className="self-start">
              Shrani spremembe
            </Button>

            <div className="flex flex-col gap-2 border-t border-line pt-3">
              {uploadLogo.isError && <Alert variant="error">{getApiErrorMessage(uploadLogo.error)}</Alert>}
              <div className="flex items-center gap-3">
                {editing.logoUrl ? (
                  <img
                    src={editing.logoUrl}
                    alt={`${editing.name} logo`}
                    className="h-12 w-12 rounded-photo border border-line object-contain"
                  />
                ) : (
                  <div className="flex h-12 w-12 items-center justify-center rounded-photo border border-dashed border-line-strong text-xs text-fg-muted">
                    Brez logotipa
                  </div>
                )}
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-fg-secondary">
                    Logotip dogodka{' '}
                    <span className="font-normal text-fg-muted">
                      (nadomesti logotip organizacije samo za to tekmovanje)
                    </span>
                  </label>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/svg+xml"
                    disabled={uploadLogo.isPending}
                    onChange={handleLogoChange}
                    className="text-sm text-fg-secondary file:mr-3 file:h-8 file:rounded-control file:border file:border-line-strong file:bg-surface-2 file:px-3 file:text-[13px] file:font-medium file:text-fg hover:file:bg-surface-3"
                  />
                </div>
              </div>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
